// @flow
import React, { useState, useEffect } from 'react';
import { Banner, Button, Select, TextStyle } from '@shopify/polaris';
import { useI18n } from '@shopify/react-i18n';

const STATUS = {
  CHANGE: 'change',
  CONNECT: 'connect',
  CANCEL: 'cancel',
};

type Property = {
  displayName: string,
  name: string,
  dataStreams: Array<Object>,
};

type Props = {
  properties: Array<Property>,
  currentPropertyName: string,
  onConnect: (selectedPropertyName: string) => void,
};

const GA4PropertySelector = ({ properties, currentPropertyName, onConnect }: Props) => {
  const [i18n] = useI18n();
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(currentPropertyName);
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    setSelectedProperty(currentPropertyName);
    setIsSelecting(false);
  }, [currentPropertyName]);

  const getStatus = () => {
    if (!isSelecting) return STATUS.CHANGE;
    if (selectedProperty === currentPropertyName) return STATUS.CANCEL;
    return STATUS.CONNECT;
  };

  const handleButtonClick = () => {
    const currentStatus = getStatus();
    switch (currentStatus) {
      case STATUS.CHANGE:
        setIsSelecting(true);
        setSelectedProperty(currentPropertyName);
        break;
      case STATUS.CANCEL:
        setIsSelecting(false);
        break;
      case STATUS.CONNECT:
        handleConnectProperty(selectedProperty);
        break;
      default:
        break;
    }
  };

  const handleConnectProperty = async (propertyName) => {
    setLoading(true);
    try {
      await onConnect(propertyName);
    } catch (error) {
      console.error('Error connecting property:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChange = (value) => {
    setSelectedProperty(value);
  };

  const status = getStatus();

  const propertyOptions = properties.map((property, index) => ({
    label: property.displayName,
    value: property.property || property.name,
    id: `property-${index}`,
  }));

  const getButtonLabel = () => {
    switch (status) {
      case STATUS.CHANGE:
        return i18n.translate('AdminHTML.GoogleAnalytics4.PropertySelector.changeProperty');
      case STATUS.CONNECT:
        return i18n.translate('AdminHTML.GoogleAnalytics4.PropertySelector.connectProperty');
      case STATUS.CANCEL:
        return i18n.translate('AdminHTML.GoogleAnalytics4.PropertySelector.cancel');
      default:
        return '';
    }
  };

  return (
    <div className="ga4-property-selector">
      {isSelecting && (
        <div className="ga4-property-selector__content">
          <div className="ga4-property-selector__banner">
            <Banner status="warning">
              {i18n.translate('AdminHTML.GoogleAnalytics4.PropertySelector.bannerMessage')}
            </Banner>
          </div>
          <div className="ga4-property-selector__select-row">
            <p className="ga4-property-selector__label">
              <TextStyle variation="subdued">
                {i18n.translate('AdminHTML.GoogleAnalytics4.PropertySelector.selectLabel')}
              </TextStyle>
            </p>
            <div className="ga4-property-selector__select">
              <Select
                label=""
                labelHidden
                options={propertyOptions}
                value={selectedProperty}
                onChange={handleSelectChange}
              />
            </div>
          </div>
        </div>
      )}
      <div className="ga4-property-selector__action">
        <Button onClick={handleButtonClick} primary={status === STATUS.CONNECT} loading={isLoading}>
          {getButtonLabel()}
        </Button>
      </div>
    </div>
  );
};

export default GA4PropertySelector;
